import React, { useCallback, useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Popover, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ENERGY_FLOW_INDICATOR_HEIGHT, ENERGY_FLOW_INDICATOR_SPACING, ICON_WITH_LABEL_HEIGHT_MOBILE } from './liveViewConstants'
import EnergyFlowIndicator from './EnergyFlowIndicator'
import { IndicatorColor, LiveItemType } from './types'
import { Variant } from '@mui/material/styles/createTypography'
import LiveViewBadge from '../../Libs/live-view/components/LiveViewBadge'
import { LiveViewItemSide } from 'src/Libs/live-view'
import { DASHBOARD_LIVE_VIEW_ITEM_PREFIX, DASHBOARD_LIVE_VIEW_VALUE_PREFIX } from 'src/Libs/qa/ids'

interface Props {
  icon: React.ReactNode
  label: string
  value: string
  directionIsReversed?: boolean
  doNotFlow?: boolean
  energyFlowIndicatorsCount: number
  rotateIcon: number
  rotateItem: number
  badgeAmount?: number
  popoverContent?: React.ReactNode
  labelVariant: Variant
  valueVariant: Variant
  inColor: IndicatorColor
  outColor: IndicatorColor
  side: LiveViewItemSide
  parentAngle: number
  overlayIcon?: React.ReactNode
  type: LiveItemType
}

interface StyleProps {
  angle?: number
  rotateIcon: number
  rotateItem: number
  height?: number
  active?: boolean
  itemsWrapperHeight?: number
  energyFlowIndicatorsCount: number
  side: LiveViewItemSide
  parentAngle: number
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  /* half of the icon + spacing*/
  const space = 30 + 12
  const energyFlowIndicatorHeight =
    props.energyFlowIndicatorsCount * ENERGY_FLOW_INDICATOR_HEIGHT + (props.energyFlowIndicatorsCount - 1) * ENERGY_FLOW_INDICATOR_SPACING
  const halfLabeledIconHeight = ICON_WITH_LABEL_HEIGHT_MOBILE / 2
  const translateX = props.side === 'left' ? -space : props.side === 'center' ? 0 : space
  const middleIconExtraSpacing = props.rotateIcon === 0 ? 62 : 0
  const translateY: number = energyFlowIndicatorHeight + halfLabeledIconHeight
  const translateYMobile: number = energyFlowIndicatorHeight + halfLabeledIconHeight + middleIconExtraSpacing

  return {
    item: {
      width: 60,
      height: 60,
      borderRadius: '50%',
      background: theme.palette.grey.grey06,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
      color: props.active ? theme.palette.liveView.liveViewItem : undefined,
    },
    iconWithAngle: {
      position: 'absolute',
      top: 0,
      /* translate to the middle of the icon, rotate and translate to the end of icon + textHeight + padding */
      transform: `translateY(${-translateY}px) rotate(${-props.parentAngle}deg) rotate(${props.rotateIcon}deg) translateY(-${
        props.rotateIcon === 90 ? 42 : 72
      }px)`,
      transformOrigin: '50% 98%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      maxWidth: 60,
      width: 60,
      overflow: 'visible',
      [theme.breakpoints.down('sm')]: {
        transform: `translateY(${-translateYMobile}px) rotate(${-props.parentAngle}deg) translateX(${translateX}px) rotate(${props.rotateIcon}deg)`,
      },
    },
    itemWithAngle: {
      transform: `rotate(${-props.rotateIcon}deg)`,
      transformOrigin: '50% calc(100% - 42px)',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        transform: `rotate(${props.rotateItem}deg)`,
      },
    },
    iconHover: {
      transition: theme.transitions.create('all'),

      '&:hover': {
        border: `1px solid ${theme.palette.liveView.liveViewItem}`,
        transition: theme.transitions.create('all'),
        cursor: 'pointer',
      },
    },
    iconPopoverOpened: {
      border: `1px solid ${theme.palette.liveView.liveViewItem}`,
    },
  }
})

const LiveViewSubItem: React.FC<Props> = ({
  icon,
  label,
  value,
  doNotFlow,
  directionIsReversed,
  energyFlowIndicatorsCount,
  rotateIcon,
  rotateItem,
  parentAngle,
  badgeAmount = 0,
  popoverContent,
  labelVariant,
  valueVariant,
  inColor,
  outColor,
  side,
  overlayIcon,
  type,
}) => {
  const { classes, cx } = useStyles({
    rotateIcon,
    rotateItem,
    active: !!value,
    energyFlowIndicatorsCount: energyFlowIndicatorsCount ?? 0,
    side,
    parentAngle,
  })
  const theme = useTheme()
  const upSm = useMediaQuery(theme.breakpoints.up('sm'))
  const requiresMaxWidth = !upSm && side !== 'center'
  const maxWidth = requiresMaxWidth ? 60 : undefined

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => (popoverContent ? setAnchorEl(event.currentTarget) : undefined),
    [popoverContent]
  )

  const handleClose = (): void => setAnchorEl(null)

  const iconClassName = useMemo(
    () => cx(classes.item, { [classes.iconHover]: !!popoverContent, [classes.iconPopoverOpened]: open }),
    [cx, classes.item, classes.iconHover, classes.iconPopoverOpened, popoverContent, open]
  )

  const iconComponent = (
    <Box className={iconClassName} onClick={event => handleClick(event)}>
      {icon}
    </Box>
  )

  return (
    <Box className={classes.iconWithAngle} data-qa={`${DASHBOARD_LIVE_VIEW_ITEM_PREFIX}${type}`}>
      <Box className={classes.itemWithAngle}>
        <Typography variant={labelVariant} align="center" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: maxWidth }}>
          {label}
        </Typography>
        <Typography variant={valueVariant} align="center" data-qa={`${DASHBOARD_LIVE_VIEW_VALUE_PREFIX}${type}`}>
          {value}
        </Typography>
        <LiveViewBadge iconComponent={iconComponent} badgeAmount={badgeAmount} overlayIcon={overlayIcon} />
      </Box>
      <EnergyFlowIndicator
        energyFlowIndicatorsCount={energyFlowIndicatorsCount}
        animate={!doNotFlow}
        reverse={directionIsReversed}
        inColor={inColor}
        outColor={outColor}
      />
      {popoverContent && (
        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          {popoverContent}
        </Popover>
      )}
    </Box>
  )
}

export default LiveViewSubItem
